
/* eslint-disable @typescript-eslint/no-explicit-any */

import { computed, defineComponent, PropType, ref, toRef, watchEffect } from 'vue';
import PanelHeader from '@/components/panel-header.vue';
import focus from '@/libs/focus';
import usePasswordValidation from '@/libs/passwordValidation';
import PwdEyeIcon from '@/components/pwd-eye-icon.vue';
import TOTPPanel from '@/components/totp-panel.vue';
import {
  LABEL_PASSWORD_RESET_KEY_INVALID,
  LABEL_PASSWORD_RESET_KEY_EXPIRED,
  LABEL_PASSWORD_RESET,
  LABEL_PASSWORD_RESET_HELP,
  LABEL_START_OVER,
  LABEL_YOUR_PASSWORD_HAS_BEEN_CHANGED,
  LABEL_CONFIRM,
} from '@/libs/i18nLabels';
import passwordState, { PasswordResetErrorCode, PasswordResetResult } from './passwordState';
import { LABEL_FIELD_INVALID, LABEL_PASSWORD, translateFormLabel } from '../libs/form';

export default defineComponent({
  components: {
    PanelHeader,
    PwdEyeIcon,
    TOTPPanel,
  },

  emits: ['retry-link'],
  props: {
    passwordMaxLength: {
      type: Number,
      required: true,
    },

    passwordKeyExpiration: {
      type: Number,
      required: true,
    },

    signInUrl: {
      type: String,
      required: true,
    },

    passwordMinLength: {
      type: Number,
      required: true,
    },

    resetInfo: {
      type: Object as PropType<PasswordResetResult>,
    },
  },

  setup(props) {
    const {
      password,
      passwordConfirm: confirm,
      isPasswordInvalid: isPwdInvalid,
      isPasswordConfirmInvalid: isPwdConfirmInvalid,
      passwordMessage,
      validate: validPassword,
    } = usePasswordValidation(props.passwordMinLength);

    const isKeyInvalid = ref(false);
    const isKeyExpired = ref(false);

    const displayTOTP = ref(false);

    const passwordSuccess = ref('');
    const signInMessage = ref('');

    function updateError(resetInfo: PasswordResetResult) {
      isKeyInvalid.value = false;
      isKeyExpired.value = false;

      // displayTOTP.value = false; //do not clear TOTP here

      if (resetInfo.errorCode) {
        displayTOTP.value = false;

        if (resetInfo.errorCode === PasswordResetErrorCode.TOTPRequired) {
          displayTOTP.value = true;
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.TOTPInvalidCode || resetInfo.errorCode === PasswordResetErrorCode.TOTPInvalidBackupCode) {
          displayTOTP.value = true;
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.InvalidPassword) {
          isPwdInvalid.value = true;
          passwordMessage.value = { label: LABEL_FIELD_INVALID, params: [{ label: LABEL_PASSWORD }] };
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.ExpiredKey || PasswordResetErrorCode.InvalidKey) {
          isKeyInvalid.value = true;
          isKeyExpired.value = resetInfo.errorCode === PasswordResetErrorCode.ExpiredKey;
        }
      }
    }

    const resetInfo = toRef(props, 'resetInfo');

    watchEffect(() => {
      if (resetInfo.value) {
        updateError(resetInfo.value);
      }
    });

    // Fix issue chen firefox suggests to save password even if there's an error (expired link etc ..)
    // display: none : save password popup is not displayed
    // not rendered : the password popup is displayed
    const showFormPanel = computed(() => {
      return !isKeyInvalid.value && !displayTOTP.value;
    });

    const renderFormPanel = computed(() => {
      return isKeyInvalid.value || displayTOTP.value || !passwordSuccess.value;
    });

    return {
      password,
      confirm,
      isPwdInvalid,
      isPwdConfirmInvalid,
      passwordMessage,
      isKeyInvalid,
      isKeyExpired,
      validPassword,
      signInMessage,
      passwordSuccess,
      showFormPanel,
      renderFormPanel,
      displayTOTP,
      LABEL_PASSWORD_RESET_KEY_INVALID,
      LABEL_PASSWORD_RESET_KEY_EXPIRED,
      LABEL_PASSWORD_RESET,
      LABEL_PASSWORD_RESET_HELP,
      LABEL_START_OVER,
    };
  },

  data() {
    return {
      processing: false,

      passwordFieldType: 'password',
      passwordFocus: false,

      confirmFieldType: 'password',
      confirmFocus: false,

      passwordReseted: false,
    };
  },

  computed: {
    passwordLabel() {
      return this.$t(LABEL_PASSWORD).toString();
    },

    isPasswordVisible() {
      return this.passwordFieldType === 'text';
    },

    isConfirmVisible() {
      return this.confirmFieldType === 'text';
    },

    confirmLabel() {
      return this.$t(LABEL_CONFIRM).toString();
    },

    passwordMessageLabel() {
      if (!this.passwordMessage || !this.passwordMessage.label) {
        return '&nbsp;'; // maintains  space
      }

      return translateFormLabel(this.passwordMessage, this.$t);
    },
  },

  methods: {
    onBlurPassword() {
      this.passwordFocus = false;
      this.validPassword('b');
    },
    onFocusPassword() {
      this.passwordFocus = true;
    },

    onBlurConfirm() {
      this.confirmFocus = false;
      this.validPassword('b');
    },
    onFocusConfirm() {
      this.confirmFocus = true;
    },

    onPasswordEyeClick() {
      this.passwordFieldType = this.passwordFieldType === 'text' ? 'password' : 'text';
      setTimeout(() => {
        const pwdElement = this.$refs.passwordField as HTMLElement;
        pwdElement.focus();
      }, 1);
    },

    onConfirmEyeClick() {
      this.confirmFieldType = this.confirmFieldType === 'text' ? 'password' : 'text';
      setTimeout(() => {
        const pwdElement = this.$refs.passwordConfirmField as HTMLElement;
        pwdElement.focus();
      }, 1);
    },

    focusField() {
      focus(this.$refs.passwordField);
    },

    /**
     */

    resetPassword() {
      this.submitPassword();
    },

    async submitPassword(TOTPCode?: string, TOTPBackupCode?: string) {
      this.validPassword('');

      if (this.isPwdConfirmInvalid) {
        //  password confirm before password
        focus(this.$refs.passwordConfirmField);
      } else if (this.isPwdInvalid) {
        focus(this.$refs.passwordField);
      }

      if (this.isPwdInvalid) {
        this.processing = false;
        return;
      }

      this.processing = true;
      //
      const res = await passwordState.resetPassword({ password: this.password, TOTPBackupCode, TOTPCode }, this.$i18n);

      if (res && !res.errorCode) {
        this.displayTOTP = false; // TOTP can be displayed
        this.signInMessage = res.signinMessage || '';
        this.passwordSuccess = this.$t(LABEL_YOUR_PASSWORD_HAS_BEEN_CHANGED);
      }
      this.processing = false;
    },

    /**
     * TOTP
     */

    onTOTPCodeSubmitted(code: string) {
      this.submitPassword(code);
    },

    onTOTPBackCodeSubmitted(backupCode: string) {
      this.submitPassword(undefined, backupCode);
    },
  },

  mounted() {
    this.focusField();
  },
});
